import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Input, App, Space, Select, Tag, Popover, Alert, List } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  UploadOutlined, DownloadOutlined, FileExcelOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import request from '../utils/request';

const roleMap = { admin: '超管', manager: '管理员', owner: '业主', tenant: '租客' };
const roleColors = { admin: 'red', manager: 'orange', owner: 'green', tenant: 'blue' };

const User = () => {
  const { message } = App.useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await request.get('/user');
      if (res.code === 200) setData(res.data.list || []);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ role: 'owner' });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone,
      role: record.role || 'owner',
      password: ''
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定删除该用户？',
      onOk: async () => {
        await request.delete('/user/' + id);
        message.success('删除成功');
        fetchData();
      }
    });
  };

  const handleSubmit = async (values) => {
    const payload = { ...values };
    if (!payload.password) delete payload.password;
    try {
      if (editingRecord) {
        await request.put('/user/' + editingRecord.id, payload);
        message.success('更新成功');
      } else {
        await request.post('/user', payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
    }
  };

  // 下载模板
  const handleDownloadTemplate = async () => {
    try {
      const res = await request.get('/import/template', { responseType: 'blob' });
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_import_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error('下载模板失败');
    }
  };

  // 文件选择
  const handleFileSelect = () => {
    fileRef.current?.click();
  };

  // 上传并预览
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      message.error('请上传 Excel 文件');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadRes = await request.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (uploadRes.code !== 200) {
        message.error('上传失败');
        return;
      }

      const previewRes = await request.post('/import/preview', {
        file_path: uploadRes.data.file_path
      });

      if (previewRes.code === 200) {
        setImportPreview(previewRes.data);
        setImportVisible(true);
      }
    } catch (error) {
      message.error('文件处理失败');
    }
    e.target.value = '';
  };

  // 确认导入
  const handleConfirmImport = async () => {
    if (!importPreview?.preview?.length) {
      message.warning('没有可导入的数据');
      return;
    }
    setImporting(true);
    try {
      const res = await request.post('/import/execute', {
        data: importPreview.preview.map(r => ({
          phone: r.data['手机号'],
          name: r.data['姓名'],
          password: r.data['密码'],
          community_name: r.data['小区名称'],
          building_name: r.data['楼栋名称'],
          unit_name: r.data['单元名称'],
          room_number: r.data['房号'],
          relation_type: r.data['关系类型'],
          is_primary: r.data['是否主业主']
        }))
      });
      if (res.code === 200) {
        message.success(`导入完成：成功 ${res.data.success_count} 条，失败 ${res.data.fail_count} 条`);
        setImportVisible(false);
        setImportPreview(null);
        fetchData();
      }
    } catch (error) {
      message.error('导入失败');
    } finally {
      setImporting(false);
    }
  };

  // 导出
  const handleExport = async () => {
    try {
      const res = await request.get('/export', { responseType: 'blob' });
      const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user_export.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
    }
  };

  const renderRooms = (rooms) => {
    if (!rooms || rooms.length === 0) {
      return <Tag color="default">未关联</Tag>;
    }
    if (rooms.length === 1) {
      const r = rooms[0];
      return <span>{r.community_name || '-'} / {r.building_name || '-'} / {r.unit_name || '-'} / {r.number || '-'}</span>;
    }
    const content = (
      <div style={{ maxWidth: 300 }}>
        {rooms.map((r, i) => (
          <div key={i} style={{ padding: '4px 0', borderBottom: i < rooms.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            {r.community_name} / {r.building_name} / {r.unit_name} / {r.number}
            {r.is_primary ? <Tag color="orange" style={{ marginLeft: 8 }}>主</Tag> : null}
            <Tag style={{ marginLeft: 4 }}>{r.relation_type === 'owner' ? '业主' : '租客'}</Tag>
          </div>
        ))}
      </div>
    );
    return (
      <Popover content={content} title="关联房号">
        <Tag color="blue" style={{ cursor: 'pointer' }}>
          <HomeOutlined /> {rooms.length}套房
        </Tag>
      </Popover>
    );
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    {
      title: '关联房号', dataIndex: 'rooms', key: 'rooms', width: 280,
      render: (rooms) => renderRooms(rooms)
    },
    {
      title: '角色', dataIndex: 'role', key: 'role', width: 80,
      render: (text) => <Tag color={roleColors[text] || 'default'}>{roleMap[text] || text}</Tag>
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 70,
      render: (text) => text === 'active'
        ? <Tag color="success">正常</Tag>
        : <Tag color="error">禁用</Tag>
    },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <input type="file" ref={fileRef} style={{ display: 'none' }}
        accept=".xlsx,.xls" onChange={handleFileChange} />

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加用户</Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>下载模板</Button>
          <Button icon={<UploadOutlined />} onClick={handleFileSelect}>批量导入</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>导出数据</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
        scroll={{ x: 900 }}
      />

      {/* 用户编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑用户' : '添加用户'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item label="手机号" name="phone" rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1\d{10}$/, message: '请输入正确的手机号' }
          ]}>
            <Input placeholder="请输入手机号" maxLength={11} />
          </Form.Item>
          <Form.Item label="密码" name="password" rules={[
            { required: !editingRecord, message: '请输入密码' },
            { min: 6, message: '密码至少6位' }
          ]}>
            <Input.Password placeholder={editingRecord ? '不填则保持原密码' : '请输入密码'} />
          </Form.Item>
          <Form.Item label="角色" name="role" rules={[{ required: true, message: '请选择角色' }]}>
            <Select>
              <Select.Option value="admin">超管</Select.Option>
              <Select.Option value="manager">管理员</Select.Option>
              <Select.Option value="owner">业主</Select.Option>
              <Select.Option value="tenant">租客</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入预览弹窗 */}
      <Modal
        title="导入预览"
        open={importVisible}
        onCancel={() => { setImportVisible(false); setImportPreview(null); }}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => { setImportVisible(false); setImportPreview(null); }}>取消</Button>,
          <Button key="import" type="primary" loading={importing}
            disabled={!importPreview?.preview?.length}
            icon={<UploadOutlined />}
            onClick={handleConfirmImport}>
            确认导入 ({importPreview?.preview?.length || 0} 条)
          </Button>
        ]}
      >
        {importPreview && (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
              <span style={{ color: '#52c41a' }}>
                <CheckCircleOutlined /> 有效数据: {importPreview.preview?.length || 0} 条
              </span>
              <span style={{ color: '#f5222d' }}>
                <CloseCircleOutlined /> 错误: {importPreview.errors?.length || 0} 条
              </span>
            </div>

            {importPreview.errors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Alert type="error" showIcon
                  message={`${importPreview.errors.length} 行数据存在错误，将跳过`}
                  description={
                    <List size="small" dataSource={importPreview.errors.slice(0, 10)}
                      renderItem={(err) => (
                        <List.Item>
                          第 {err.row} 行: {err.errors?.join('；')}
                        </List.Item>
                      )}
                    />
                  }
                />
              </div>
            )}

            {importPreview.preview?.length > 0 && (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <Table
                  size="small"
                  dataSource={importPreview.preview.slice(0, 20)}
                  rowKey="row"
                  pagination={false}
                  columns={[
                    { title: '行', dataIndex: 'row', width: 50 },
                    { title: '姓名', dataIndex: ['data', '姓名'], width: 80 },
                    { title: '手机号', dataIndex: ['data', '手机号'], width: 120 },
                    { title: '小区', dataIndex: ['data', '小区名称'], width: 100 },
                    { title: '楼栋', dataIndex: ['data', '楼栋名称'], width: 80 },
                    { title: '单元', dataIndex: ['data', '单元名称'], width: 80 },
                    { title: '房号', dataIndex: ['data', '房号'], width: 60 },
                    { title: '关系', dataIndex: ['data', '关系类型'], width: 60 }
                  ]}
                />
                {importPreview.preview.length > 20 && (
                  <div style={{ textAlign: 'center', padding: 8, color: '#999' }}>
                    仅显示前 20 条，共 {importPreview.preview.length} 条有效数据
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default User;
